import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Download, Building2, Trash2, Merge } from "lucide-react";

const STATUS_LABEL = { new: "New", in_progress: "In Progress", on_hold: "On Hold", completed: "Completed", cancelled: "Cancelled" };
const STATUS_COLOR = {
  new: "bg-slate-100 text-slate-800 border-slate-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  on_hold: "bg-purple-100 text-purple-800 border-purple-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};
const BAR_COLORS = ["#0F172A", "#D97706", "#16A34A", "#DC2626", "#0EA5E9", "#7C3AED", "#EAB308", "#EC4899"];
const KPI_ITEMS = [
  ["Total Requests", "total", "bg-white border-slate-200"],
  ["Open", "open", "bg-amber-50 border-amber-200"],
  ["Completed", "completed", "bg-emerald-50 border-emerald-200"],
  ["Overdue", "overdue", "bg-red-50 border-red-200"],
];

function KpiRow({ stats }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="company-kpi-row">
      {KPI_ITEMS.map(([label, key, tone]) => (
        <div key={label} className={`rounded-lg border p-4 ${tone}`}>
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
          <div className="text-3xl font-semibold tracking-tight mt-1 text-slate-900">{stats[key]}</div>
        </div>
      ))}
    </div>
  );
}

function IssueBreakdown({ stats, sortedIssues, sortedProperties, maxIssue }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="border-b border-slate-100">
        <div className="text-[11px] font-mono uppercase tracking-widest text-amber-600 mb-1">Issues</div>
        <CardTitle className="text-lg">Issues {stats.company} has had</CardTitle>
      </CardHeader>
      <CardContent className="p-5 space-y-3" data-testid="company-issue-breakdown">
        {sortedIssues.map(([issue, count], i) => (
          <div key={issue} className="space-y-1" data-testid={`company-issue-row-${issue.replace(/\s+/g, "-")}`}>
            <div className="flex justify-between text-sm">
              <span className="text-slate-800">{issue}</span>
              <span className="font-mono text-slate-500">{count}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${(count / maxIssue) * 100}%`, background: BAR_COLORS[i % BAR_COLORS.length] }} />
            </div>
          </div>
        ))}
        {Object.keys(stats.by_issue).length === 0 && <p className="text-sm text-slate-500">No issues on record.</p>}
        <div className="pt-3 border-t border-slate-100 flex flex-wrap gap-2">
          {sortedProperties.map(([prop, count]) => (
            <span key={prop} className="text-xs font-mono bg-slate-900 text-white rounded px-2 py-1" data-testid={`company-property-${prop.replace(/\s+/g, "-")}`}>
              {prop} · {count}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function RecentRequests({ orders }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="border-b border-slate-100">
        <div className="text-[11px] font-mono uppercase tracking-widest text-amber-600 mb-1">History</div>
        <CardTitle className="text-lg">Recent Requests</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {orders.map((o) => (
          <div key={o.id} className="px-4 py-3 border-b border-slate-100 last:border-0" data-testid={`company-order-${o.ticket_no}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-xs text-slate-500">{o.ticket_no}</span>
              <Badge className={`border ${STATUS_COLOR[o.status] || STATUS_COLOR.new}`}>{STATUS_LABEL[o.status] || o.status}</Badge>
            </div>
            <div className="text-sm text-slate-800 mt-1"><strong>{o.issue_type}</strong> — {o.office_area}</div>
            <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{o.description}</div>
          </div>
        ))}
        {orders.length === 0 && <div className="p-6 text-sm text-slate-500 text-center">No requests from this company yet.</div>}
      </CardContent>
    </Card>
  );
}

export default function CompanyStats({ api }) {
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reassignTo, setReassignTo] = useState("");
  const [merging, setMerging] = useState(false);

  const fetchCompanies = useCallback(async () => {
    try { const r = await axios.get(`${api}/companies`); setCompanies(r.data.companies); }
    catch (e) { toast.error("Failed to load companies"); }
  }, [api]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  useEffect(() => {
    if (!company) { setStats(null); return; }
    (async () => {
      setLoading(true);
      try { const r = await axios.get(`${api}/stats/company`, { params: { company } }); setStats(r.data); }
      catch (e) { toast.error("Failed to load company stats"); }
      finally { setLoading(false); }
    })();
  }, [api, company]);

  const exportCsv = () => window.open(`${api}/work-orders/export.csv?company=${encodeURIComponent(company)}`, "_blank");

  const mergeDuplicates = async () => {
    setMerging(true);
    try {
      const r = await axios.post(`${api}/companies/merge-duplicates`);
      const merged = r.data.merged || [];
      if (merged.length) toast.success(`Merged ${merged.length} duplicate spelling(s)`);
      else toast.info("No duplicate companies found");
      if (merged.some((m) => m.from === company)) setCompany("");
      await fetchCompanies();
    } catch { toast.error("Merge failed"); }
    finally { setMerging(false); }
  };

  const confirmDeleteCompany = async () => {
    if (!company || !reassignTo) { toast.error("Pick a company to receive the work orders"); return; }
    try {
      const r = await axios.delete(`${api}/companies/${encodeURIComponent(company)}`, { params: { reassign_to: reassignTo } });
      toast.success(`Moved ${r.data.reassigned} ticket(s) to ${reassignTo} and removed ${company}`);
      setDeleting(false);
      setReassignTo("");
      setCompany("");
      await fetchCompanies();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete company"); }
  };

  const maxIssue = stats ? Math.max(1, ...Object.values(stats.by_issue)) : 1;
  const sortedIssues = useMemo(
    () => (stats ? Object.entries(stats.by_issue).sort((a, b) => b[1] - a[1]) : []),
    [stats]
  );
  const sortedProperties = useMemo(
    () => (stats ? Object.entries(stats.by_property).sort((a, b) => b[1] - a[1]) : []),
    [stats]
  );

  return (
    <div className="space-y-6" data-testid="company-stats-panel">
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-500 shrink-0">
            <Building2 className="w-4 h-4 text-amber-600" />Company Report
          </div>
          <Select value={company} onValueChange={setCompany}>
            <SelectTrigger className="md:w-[320px] bg-white" data-testid="company-select">
              <SelectValue placeholder="Select a company" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {companies.map((c) => (<SelectItem key={c} value={c} data-testid={`company-option-${c.replace(/\s+/g, "-")}`}>{c}</SelectItem>))}
            </SelectContent>
          </Select>
          {company && (
            <Button onClick={exportCsv} variant="outline" className="bg-white" data-testid="company-export-csv-button">
              <Download className="w-4 h-4 mr-2" />Export CSV
            </Button>
          )}
          {company && (
            <Button onClick={() => { setReassignTo(""); setDeleting(true); }} variant="outline" className="bg-white text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-company-button">
              <Trash2 className="w-4 h-4 mr-2" />Delete Company
            </Button>
          )}
          <Button onClick={mergeDuplicates} disabled={merging} variant="outline" className="bg-white" data-testid="merge-duplicates-button">
            <Merge className="w-4 h-4 mr-2" />{merging ? "Merging..." : "Merge Duplicates"}
          </Button>
        </CardContent>
      </Card>

      {!company && (
        <div className="text-center py-10 text-slate-500 bg-white border border-dashed border-slate-200 rounded-lg" data-testid="company-empty-state">
          Pick a company to see its issue history and stats.
        </div>
      )}

      {company && loading && <div className="text-sm text-slate-500 text-center py-6">Loading...</div>}

      {company && stats && !loading && (
        <>
          <KpiRow stats={stats} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <IssueBreakdown stats={stats} sortedIssues={sortedIssues} sortedProperties={sortedProperties} maxIssue={maxIssue} />
            <RecentRequests orders={stats.orders} />
          </div>
        </>
      )}

      <Dialog open={deleting} onOpenChange={(open) => { if (!open) setDeleting(false); }}>
        <DialogContent className="bg-white" data-testid="delete-company-dialog">
          <DialogHeader>
            <DialogTitle>Delete {company}</DialogTitle>
            <DialogDescription>
              Work orders are kept — pick the company they should move to. {company} will then disappear from reports.
            </DialogDescription>
          </DialogHeader>
          <Select value={reassignTo} onValueChange={setReassignTo}>
            <SelectTrigger className="bg-white" data-testid="reassign-company-select">
              <SelectValue placeholder="Reassign work orders to..." />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {companies.filter((c) => c !== company).map((c) => (
                <SelectItem key={c} value={c} data-testid={`reassign-option-${c.replace(/\s+/g, "-")}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleting(false)} data-testid="delete-company-cancel-button">Cancel</Button>
            <Button onClick={confirmDeleteCompany} disabled={!reassignTo} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-company-button">Delete & Reassign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
